import { Box, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, PieSectorDataItem, ResponsiveContainer, Sector, Tooltip } from 'recharts';
import dashboardApi from '~/apis/dashboard';
import { useResponsiveValue } from '~/hooks/useResponsiveValue';
import { handleApiError } from '~/utils/helper';
import { IResultGetAlertStats } from '~/utils/interface/dashboard';
import { IUser } from '~/utils/interface/user';

type AlertStatsSummaryProps = {
  alertStats?: IResultGetAlertStats;
  currentUser?: IUser;
};

const AlertStatsSummary = (props: AlertStatsSummaryProps) => {
  const { t } = useTranslation('admin/home');

  const [alertStatsData, setAlertStatsData] = useState<IResultGetAlertStats | undefined>();

  const [data, setData] = useState<
    Array<{ label: string; name: string; value: number; color: string; isEmpty?: boolean }>
  >([]);

  const [total, setTotal] = useState<number>(0);

  const responsiveFontSize = useResponsiveValue({
    lg: '12px',
    xs: '10px',
    xl: '14px',
  });

  const responsiveLabel = useResponsiveValue({
    lg: {
      fontSize: '12px',
      textPosition: 15,
      ex: 50,
    },
    xl: {
      fontSize: '12px',
      textPosition: 26,
      ex: 80,
    },
  });

  useEffect(() => {
    if (!props.currentUser?.username) return;

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const response = await dashboardApi.getAlertStas({ username: props.currentUser?.username });
        if (!isCancelled) {
          const next = response.data.result ?? undefined;
          setAlertStatsData((prev) => {
            if (JSON.stringify(prev) === JSON.stringify(next)) {
              return prev;
            }
            return next;
          });
        }
      } catch (error) {
        if (!isCancelled) handleApiError(error);
      } finally {
        if (!isCancelled) {
          timeoutId = setTimeout(poll, 10000); // 10s
        }
      }
    };

    poll();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [props.currentUser?.username]);

  useEffect(() => {
    const newData = [
      {
        label: t('alertStats.remove_device'),
        name: `${t('alertStats.title')} ${t('alertStats.remove_device').toLowerCase()}`,
        value: alertStatsData?.remove_device ?? 0,
        color: '#7C3AED',
      },
      {
        label: t('alertStats.geofence'),
        name: `${t('alertStats.title')} ${t('alertStats.geofence').toLowerCase()}`,
        value: alertStatsData?.geofence ?? 0,
        color: '#EF4444',
      },
      {
        label: t('alertStats.gathering'),
        name: `${t('alertStats.title')} ${t('alertStats.gathering').toLowerCase()}`,
        value: alertStatsData?.gathering ?? 0,
        color: '#C54D09',
      },
      {
        label: t('alertStats.offline'),
        name: `${t('alertStats.title')} ${t('alertStats.offline').toLowerCase()}`,
        value: alertStatsData?.offline ?? 0,
        color: '#F97316',
      },
    ];

    const filtered = newData.filter((item) => item.value !== 0);
    const total = filtered?.reduce((s, item) => s + item.value, 0);

    setTotal(total);

    if (total === 0)
      setData([
        {
          label: '',
          name: '',
          value: 1,
          color: '#E5E7EB',
          isEmpty: true,
        },
      ]);
    else setData(filtered);
  }, [t, alertStatsData]);

  const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, payload, responsiveLabel }: any) => {
    if (!payload?.value) return;

    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    const sx = cx + outerRadius * cos;
    const sy = cy + (outerRadius + 5) * sin;
    const mx = cx + (outerRadius + 10) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * (responsiveLabel?.ex ?? 60);
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    const textPosition = responsiveLabel?.textPosition ?? 0;

    return (
      <g>
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={payload?.fill} fill='none' />
        <text
          x={(cos >= 0 ? mx + textPosition - 25 : mx - textPosition + 25) + (cos >= 0 ? 1 : -1) * 12}
          y={ey - 5}
          textAnchor={textAnchor}
          fill='#161A1F'
          fontSize={responsiveLabel?.fontSize}
        >
          {payload?.label}
        </text>
        <text
          x={(cos >= 0 ? mx + textPosition : mx - textPosition) + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={15}
          textAnchor={textAnchor}
          fill={payload?.fill}
          fontWeight={600}
          fontSize={responsiveLabel?.fontSize}
        >
          {payload?.value}
        </text>
      </g>
    );
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }

    if (num >= 100000) {
      return `${(num / 100000).toFixed(1)}K`;
    }

    return num.toString();
  };

  const renderActiveShape = (props: PieSectorDataItem) => {
    const { cx = 0, cy = 0, innerRadius = 0, outerRadius = 0, startAngle, endAngle, fill } = props;

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  const formatPercent = (value: number) => {
    const percent = (value / total) * 100;
    if (percent < 1) return `${percent.toFixed(3)}%`;

    return `${percent.toFixed(2)}%`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0];
    if (data.payload.isEmpty) return null;

    return (
      <Box
        className='rounded-md bg-white p-2 shadow-lg flex justify-between items-center'
        sx={{
          border: '1px solid ' + data.color,
        }}
      >
        <Box className='mr-1'>{data.payload.label}:</Box>
        <Box className='tooltip-value'>
          {data.value} ({formatPercent(data.value)})
        </Box>
      </Box>
    );
  };

  return (
    <Box className='rounded-xl bg-white flex flex-col'>
      <Typography className='bg-[#123B6D] text-white font-semibold text-xl rounded-t-xl p-3'>
        {t('alertStats.title')}
      </Typography>
      <Box
        className='w-full relative'
        style={{
          width: '100%',
          height: 320,
        }}
      >
        <ResponsiveContainer>
          <PieChart>
            <Pie
              minAngle={30}
              data={data}
              dataKey='value'
              nameKey='name'
              cx='50%'
              cy='45%'
              innerRadius='20%'
              outerRadius='60%'
              startAngle={90}
              endAngle={-270}
              fill='#82ca9d'
              isAnimationActive={false}
              labelLine={false}
              className='hover:cursor-pointer'
              activeShape={data?.[0]?.isEmpty ? undefined : renderActiveShape}
              label={(prev) => (prev?.payload?.isEmpty ? null : renderCustomLabel({ ...prev, responsiveLabel }))}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <text
                x='50%'
                y='46%'
                textAnchor='middle'
                dominantBaseline='middle'
                fontSize={18}
                fontWeight='500'
                fill='#404854'
              >
                {formatNumber(total)}
              </text>
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {!data?.[0]?.isEmpty && (
          <Box className={`absolute left-0 w-full ${data.length <= 2 ? 'bottom-4' : 'bottom-1'}`}>
            <ul className='flex flex-wrap justify-center gap-x-4 gap-y-2 m-0 p-0 list-none'>
              {data.map((item, index) => (
                <li
                  key={index}
                  className='text-[#161A1F] min-[1880px]:w-[35%] leading-2'
                  style={{ fontSize: responsiveFontSize ?? '10px' }}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: item.color,
                      marginRight: 6,
                      borderRadius: 2,
                      display: 'inline-block',
                    }}
                  />
                  {item.name}
                </li>
              ))}
            </ul>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default memo(AlertStatsSummary);
